import { useCallback, useMemo } from 'react';
import { useIsMutating, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import {
  FavoritesResponseSchema,
  SignalChipSizeResponseSchema,
  type FavoriteEntity,
  type FavoriteInput,
  type FavoriteScope,
  type FavoritesResponse,
  type SignalChipSize,
} from '@shared/flow/signal-chips';
import { FAVORITES_QUERY_KEY, favoritesQueryKey } from './useFavorites';
import { FAVORITE_COUNTS_QUERY_KEY } from './useFavoriteCounts';
import { invalidateExcludingSignalLists } from '../signals/useSignalsQueries';

/**
 * Every favorites entry (all `viewAs` values), every counts entry (all tabs) and every Signals list that excludes the
 * viewer's favorites ("All else"): what a favorite or view write staleness-marks.
 */
export function invalidateFavoriteCaches(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: FAVORITE_COUNTS_QUERY_KEY }),
    invalidateExcludingSignalLists(queryClient),
  ]);
}

/**
 * Every favorite mutation answers with the refreshed GET payload in the caller's own context (viewAs null), so it
 * replaces the unscoped favorites cache directly; every favorites and counts entry (all tabs, all `viewAs`
 * values) is then invalidated. Errors are returned to the caller (no toast here: mutations.md).
 */
function useWriteFavoritesResponse() {
  const queryClient = useQueryClient();
  return useCallback(
    async (data: FavoritesResponse) => {
      queryClient.setQueryData(favoritesQueryKey(null), data);
      await invalidateFavoriteCaches(queryClient);
    },
    [queryClient],
  );
}

const jsonInit = (method: 'POST' | 'PUT', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

type ItemOp = { kind: 'update'; input: FavoriteInput } | { kind: 'delete' };

/**
 * Editing or deleting one favorite. Both share the mutation scope `favorite:${id}`, so an edit and a delete of the
 * same favorite are serialised (FIFO in the QueryClient's MutationCache) and the queue outlives any editor mount:
 * a Done or trash press from a remounted editor still queues behind a write the previous one left in flight. A
 * failed write still releases the next one. Different favorites never wait on each other. `pending` is this hook
 * instance's own request only.
 */
export function useFavoriteItemMutation({ scope, id }: { scope: FavoriteScope; id: string }) {
  const { apiFetch } = useApi();
  const writeResponse = useWriteFavoritesResponse();

  const mutation = useMutation({
    scope: { id: `favorite:${id}` },
    mutationFn: (op: ItemOp): Promise<FavoritesResponse> => {
      const url = `/api/flow/favorites/${scope}/${encodeURIComponent(id)}`;
      return fetchJson(apiFetch, FavoritesResponseSchema, url, op.kind === 'update' ? jsonInit('PUT', op.input) : { method: 'DELETE' });
    },
    onSuccess: writeResponse,
  });

  const { mutateAsync } = mutation;
  const updateFavorite = useCallback((input: FavoriteInput) => mutateAsync({ kind: 'update', input }), [mutateAsync]);
  const deleteFavorite = useCallback(() => mutateAsync({ kind: 'delete' }), [mutateAsync]);
  return useMemo(
    () => ({ updateFavorite, deleteFavorite, pending: mutation.isPending }),
    [updateFavorite, deleteFavorite, mutation.isPending],
  );
}

/**
 * Adding a favorite to one tab in one scope. Adds to the same scope + tab share the mutation scope
 * `favorite:add:${scope}:${entity}`, so two quick adds reach the server in order and the per-tab limit is checked
 * against the first one's result; adds for another tab or scope are not held.
 */
export function useAddFavoriteMutation({ scope, entity }: { scope: FavoriteScope; entity: FavoriteEntity }) {
  const { apiFetch } = useApi();
  const writeResponse = useWriteFavoritesResponse();

  const mutation = useMutation({
    scope: { id: `favorite:add:${scope}:${entity}` },
    mutationFn: (input: FavoriteInput): Promise<FavoritesResponse> =>
      fetchJson(apiFetch, FavoritesResponseSchema, `/api/flow/favorites/${scope}`, jsonInit('POST', input)),
    onSuccess: writeResponse,
  });

  const { mutateAsync } = mutation;
  const addFavorite = useCallback((input: FavoriteInput) => mutateAsync(input), [mutateAsync]);
  return useMemo(() => ({ addFavorite, pending: mutation.isPending }), [addFavorite, mutation.isPending]);
}

const SIZE_MUTATION_KEY = ['favorite-size'] as const;

/**
 * Runs a favorites-cache write and re-marks every entry that was invalidated before it. `setQueryData` clears
 * `isInvalidated`, and an inactive viewAs entry flagged by a favorite edit has no observer to refetch it: losing
 * the flag would leave stale favorites under `staleTime: Infinity`.
 */
function invalidatedFavoritesKeys(queryClient: QueryClient) {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: FAVORITES_QUERY_KEY })
    .filter(q => q.state.isInvalidated)
    .map(q => q.queryKey);
}

/** `alreadyFlagged`: keys captured before a `cancelQueries`, which reverts an in-flight inactive refetch and drops its flag. */
function writeKeepingInvalidation(queryClient: QueryClient, write: () => void, alreadyFlagged: readonly (readonly unknown[])[] = []) {
  const flagged = [...alreadyFlagged, ...invalidatedFavoritesKeys(queryClient)];
  write();
  for (const queryKey of flagged) void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
}

/**
 * The favorites size is its own server preference, one for every tab. Optimistic: the rows flip at once across
 * every cached viewAs entry, roll back on failure, and never refetch favorites or counts. One save at a time:
 * `putFavoritesSize` is a no-op while one is in flight (the UI also disables both controls via `sizePending`), so
 * there is no ordering to manage. Rollback patches ONLY `size`; favorite edits made meanwhile are never reverted.
 */
export function useFavoriteSizeMutation() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const putSize = useMutation({
    mutationKey: SIZE_MUTATION_KEY,
    mutationFn: (size: SignalChipSize) =>
      fetchJson(apiFetch, SignalChipSizeResponseSchema, '/api/flow/favorites/size', jsonInit('PUT', { size })),
    onMutate: async size => {
      const flagged = invalidatedFavoritesKeys(queryClient);
      await queryClient.cancelQueries({ queryKey: FAVORITES_QUERY_KEY });
      const previousSizes = queryClient
        .getQueriesData<FavoritesResponse>({ queryKey: FAVORITES_QUERY_KEY })
        .flatMap(([key, data]) => (data ? [[key, data.size] as const] : []));
      writeKeepingInvalidation(
        queryClient,
        () => queryClient.setQueriesData<FavoritesResponse>({ queryKey: FAVORITES_QUERY_KEY }, old => old && { ...old, size }),
        flagged,
      );
      return { previousSizes };
    },
    onError: (_err, _size, context) => {
      writeKeepingInvalidation(queryClient, () => {
        for (const [key, previous] of context?.previousSizes ?? []) {
          queryClient.setQueryData<FavoritesResponse>(key, old => old && { ...old, size: previous });
        }
      });
    },
    onSuccess: ({ size }) => {
      writeKeepingInvalidation(queryClient, () =>
        queryClient.setQueriesData<FavoritesResponse>({ queryKey: FAVORITES_QUERY_KEY }, old => old && { ...old, size }),
      );
    },
  });

  // Cache-wide, like the guard in putFavoritesSize: a save started by an unmounted hook instance still counts.
  const sizePending = useIsMutating({ mutationKey: SIZE_MUTATION_KEY }) > 0;

  const putFavoritesSize = useCallback(
    (size: SignalChipSize): Promise<unknown> =>
      // One synchronous cache read enforces one outstanding save.
      queryClient.isMutating({ mutationKey: SIZE_MUTATION_KEY }) > 0 ? Promise.resolve() : putSize.mutateAsync(size),
    [queryClient, putSize],
  );

  return useMemo(
    () => ({ putFavoritesSize, sizeError: putSize.error, sizePending }),
    [putFavoritesSize, putSize.error, sizePending],
  );
}
